import { useState, useEffect } from 'react'
import { lumi } from '../lib/lumi'

export function useMicrocuencasVariablesCRUD() {
  const [microcuencas, setMicrocuencas] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    try {
      const result = await lumi.entities.microcuencas_variables.list({
        sort: { codigo: 1 }
      })
      setMicrocuencas(result.list || [])
      console.log('Microcuencas variables cargadas:', result.list?.length || 0)
    } catch (error) {
      console.error('Error al cargar microcuencas variables:', error)
      setMicrocuencas([])
    } finally {
      setLoading(false)
    }
  }

  const createMicrocuenca = async (data: any) => {
    try {
      const newData = {
        ...data,
        creator: 'system',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
      await lumi.entities.microcuencas_variables.create(newData)
      console.log('Microcuenca variable creada')
      await fetchData()
    } catch (error) {
      console.error('Error al crear microcuenca variable:', error)
      throw error
    }
  }

  const updateMicrocuenca = async (id: string, data: any) => {
    try {
      await lumi.entities.microcuencas_variables.update(id, {
        ...data,
        updatedAt: new Date().toISOString()
      })
      console.log('Microcuenca variable actualizada:', id)
      await fetchData()
    } catch (error) {
      console.error('Error al actualizar microcuenca variable:', error)
      throw error
    }
  }

  const deleteMicrocuenca = async (id: string) => {
    try {
      await lumi.entities.microcuencas_variables.delete(id)
      console.log('Microcuenca variable eliminada:', id)
      await fetchData()
    } catch (error) {
      console.error('Error al eliminar microcuenca variable:', error)
      throw error
    }
  }

  const parseCSVContent = (text: string) => {
    const lines = text.split('\n').filter(line => line.trim())
    
    // Dividir por tabuladores ya que los datos están separados por tabs
    const headers = lines[0].split('\t').map(h => h.trim().replace(/"/g, ''))
    
    const records = []
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split('\t').map(v => v.trim().replace(/"/g, '').replace(/,/g, ''))
      
      if (values.length >= 3) { // Al menos debe tener código, municipio y microcuenca
        const record: any = {
          creator: 'system',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
        
        // Mapeo manual de columnas según el formato proporcionado
        record.codigo = values[0] || ''
        record.municipio = values[1] || ''
        record.microcuenca = values[2] || ''
        record.region = values[3] || ''
        record.priorizacion_ranking = parseInt(values[4]) || 0
        record.priorizacion_valor = parseFloat(values[5]) || 0
        record.categoria = values[6] || ''
        record.densidad_poblacion = parseFloat(values[7]) || 0
        record.rezago_social = parseFloat(values[8]) || 0
        record.superficie_microcuenca = parseFloat(values[9]) || 0
        record.superficie_forestal = parseFloat(values[10]) || 0
        record.superficie_pastizal = parseFloat(values[11]) || 0
        record.superficie_agricola = parseFloat(values[12]) || 0
        record.superficie_asentamiento = parseFloat(values[13]) || 0
        record.superficie_cafe = parseFloat(values[14]) || 0
        record.superficie_incendios = parseFloat(values[15]) || 0
        record.tiempo_concentracion = parseFloat(values[16]) || 0
        record.pendiente = parseFloat(values[17]) || 0
        record.indice_gravelius = parseFloat(values[18]) || 0
        record.etp = parseFloat(values[19]) || 0
        record.deforestacion = parseFloat(values[20]) || 0
        record.erosion = parseFloat(values[21]) || 0
        record.escurrimiento = parseFloat(values[22]) || 0
        record.precipitacion = parseFloat(values[23]) || 0
        
        records.push(record)
      }
    }
    return records
  }

  const uploadCSVFile = async (file: File) => {
    setUploading(true)
    try {
      const text = await file.text()
      
      if (!text || text.trim().length === 0) {
        throw new Error('El archivo está vacío')
      }
      
      const records = parseCSVContent(text)
      
      if (records.length === 0) {
        throw new Error('No se encontraron registros válidos en el archivo')
      }
      
      console.log(`Importando ${records.length} registros...`)
      for (const record of records) {
        await lumi.entities.microcuencas_variables.create(record)
      }
      
      await fetchData()
      alert(`✅ ${records.length} registros importados exitosamente`)
    } catch (error: any) {
      console.error('Error al importar CSV:', error)
      alert(`❌ Error: ${error.message || 'No se pudo importar el archivo CSV'}`)
    } finally {
      setUploading(false)
    }
  }

  const uploadCSVFromURL = async (url: string) => {
    setUploading(true)
    try {
      const response = await fetch(url)
      
      if (!response.ok) {
        throw new Error(`Error al descargar: ${response.status} ${response.statusText}`)
      }
      
      const text = await response.text()
      
      if (!text || text.trim().length === 0) {
        throw new Error('El archivo descargado está vacío')
      }
      
      const records = parseCSVContent(text)
      
      if (records.length === 0) {
        throw new Error('No se encontraron registros válidos en el archivo')
      }
      
      console.log(`Importando ${records.length} registros...`)
      for (const record of records) {
        await lumi.entities.microcuencas_variables.create(record)
      }
      
      await fetchData()
      alert(`✅ ${records.length} registros importados exitosamente`)
    } catch (error: any) {
      console.error('Error al importar CSV:', error)
      alert(`❌ Error: ${error.message || 'No se pudo importar el archivo CSV'}`)
    } finally {
      setUploading(false)
    }
  }

  const clearAllData = async () => {
    try {
      for (const item of microcuencas) {
        await lumi.entities.microcuencas_variables.delete(item._id)
      }
      await fetchData()
      alert('✅ Base de datos limpiada')
    } catch (error) {
      console.error('Error al limpiar datos:', error)
      alert('❌ Error al limpiar la base de datos')
    }
  }

  return {
    microcuencas,
    loading,
    uploading,
    fetchData,
    createMicrocuenca,
    updateMicrocuenca,
    deleteMicrocuenca,
    uploadCSVFile,
    uploadCSVFromURL,
    clearAllData
  }
}
